import { useMemo, useState } from 'react';
import {
  addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format,
  isSameDay, isSameMonth, parseISO, startOfMonth, startOfWeek, subMonths
} from 'date-fns';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useTasks } from '@/hooks/useTasks';
import { useAppStore } from '@/store/appStore';
import { applyFilters } from '@/components/filters/FilterPanel';

export function CalendarView() {
  const [cursor, setCursor] = useState(new Date());
  const selectTask = useAppStore(s => s.selectTask);
  const filters = useAppStore(s => s.filters);
  const { data: tasks = [] } = useTasks('status');

  const filteredTasks = useMemo(() => applyFilters(tasks, filters), [tasks, filters]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const tasksByDay = useMemo(() => {
    const m = new Map<string, typeof filteredTasks>();
    for (const t of filteredTasks) {
      if (!t.fechaProyectada) continue;
      const k = format(parseISO(t.fechaProyectada), 'yyyy-MM-dd');
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    return m;
  }, [filteredTasks]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(subMonths(cursor, 1))} className="p-1 hover:bg-stone-100 rounded">
            <CaretLeft size={16} weight="bold" />
          </button>
          <span className="text-base font-semibold capitalize">{format(cursor, 'MMMM yyyy')}</span>
          <button onClick={() => setCursor(addMonths(cursor, 1))} className="p-1 hover:bg-stone-100 rounded">
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
        <button onClick={() => setCursor(new Date())} className="text-sm text-blue-600 hover:underline">
          Hoy
        </button>
      </div>

      <div className="grid grid-cols-7 border-b border-stone-200 text-xs font-medium text-stone-600">
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
          <div key={d} className="px-2 py-2 text-center">{d}</div>
        ))}
      </div>

      <div className="flex-1 grid grid-cols-7 grid-rows-6 overflow-auto">
        {days.map(d => {
          const isCurrentMonth = isSameMonth(d, cursor);
          const isToday = isSameDay(d, new Date());
          const dayTasks = tasksByDay.get(format(d, 'yyyy-MM-dd')) ?? [];
          return (
            <div
              key={d.toISOString()}
              className={`border-b border-r border-stone-100 p-1 min-h-[80px] ${isCurrentMonth ? 'bg-white' : 'bg-stone-50 text-stone-400'}`}
            >
              <div className={`text-xs font-medium mb-1 ${isToday ? 'inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white' : ''}`}>
                {format(d, 'd')}
              </div>
              <div className="space-y-0.5">
                {dayTasks.slice(0, 3).map(t => (
                  <div
                    key={t.idTareaArq}
                    onClick={() => selectTask(t.idTareaArq)}
                    className="text-[10px] truncate rounded px-1 py-0.5 cursor-pointer hover:opacity-80"
                    style={{ background: t.colorEstado || '#888', color: '#fff' }}
                  >
                    {t.nombreTarea}
                  </div>
                ))}
                {dayTasks.length > 3 && (
                  <div className="text-[10px] text-stone-500">+{dayTasks.length - 3} más</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}